import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Squares2X2Icon,
    ClipboardDocumentListIcon,
    DocumentTextIcon,
    ExclamationTriangleIcon,
    TagIcon,
    PlusIcon,
    ArrowPathIcon,
    ClockIcon,
    CalendarDaysIcon,
    CheckCircleIcon,
    MagnifyingGlassIcon,
    MapPinIcon,
    FunnelIcon,
    InboxIcon,
    UserIcon,
    BugAntIcon,
    PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToDataInvalidation } from '../utils/api';
import {
    fetchOverview,
    fetchAssignments,
    fetchReports,
    fetchActivityTypes,
    fetchFarmers,
    fetchFarmPlots,
    fetchStaffUsers,
    createAssignment,
    saveActivityType,
} from '../utils/activityApi';
import { isActivityManager, canCreateAssignments, canReviewReports } from '../utils/activityRoles';
import AssignmentFormDrawer from '../components/activities/AssignmentFormDrawer';
import AssignmentDetailDrawer from '../components/activities/AssignmentDetailDrawer';
import ReportReviewDrawer from '../components/activities/ReportReviewDrawer';
import ActivityTypeDrawer from '../components/activities/ActivityTypeDrawer';
import ActivityStatusBadge from '../components/activities/ActivityStatusBadge';
import ActivityPriorityBadge from '../components/activities/ActivityPriorityBadge';
import type {
    ActivitiesOverview,
    ActivityReport,
    ActivityType,
    Assignment,
    FarmerOption,
    FarmField,
    Observation,
    StaffUser,
} from '../components/activities/types';

type TabKey = 'overview' | 'assignments' | 'reports' | 'observations' | 'types';

const TABS: Array<{ key: TabKey; label: string; icon: React.ElementType }> = [
    { key: 'overview', label: 'Overview', icon: Squares2X2Icon },
    { key: 'assignments', label: 'Assignments', icon: ClipboardDocumentListIcon },
    { key: 'reports', label: 'Reports', icon: DocumentTextIcon },
    { key: 'observations', label: 'Observations', icon: BugAntIcon },
    { key: 'types', label: 'Activity Types', icon: TagIcon },
];

const ASSIGNMENT_STATUSES = [
    'draft',
    'assigned',
    'accepted',
    'in_progress',
    'submitted',
    'awaiting_review',
    'correction_required',
    'resubmitted',
    'approved',
    'rejected',
    'cancelled',
    'closed',
] as const;

const REPORT_STATUSES = [
    'draft',
    'pending_sync',
    'submitted',
    'awaiting_review',
    'correction_required',
    'resubmitted',
    'approved',
    'rejected',
] as const;

const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;

const TERMINAL_STATUSES = ['approved', 'rejected', 'cancelled', 'closed'];

const STATUS_COLORS: Record<string, string> = {
    draft: 'bg-slate-400',
    assigned: 'bg-blue-500',
    accepted: 'bg-sky-500',
    in_progress: 'bg-brand-blue',
    submitted: 'bg-amber-400',
    awaiting_review: 'bg-amber-500',
    correction_required: 'bg-orange-500',
    resubmitted: 'bg-violet-500',
    approved: 'bg-emerald-500',
    rejected: 'bg-red-500',
    cancelled: 'bg-gray-400',
    closed: 'bg-slate-600',
};

const inputClass =
    'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20';

const labelClass = 'mb-1 block text-[10px] font-black uppercase tracking-wide text-slate-500';

const formatDate = (value?: string): string => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (value?: string): string => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const isSameDay = (value?: string, target = new Date()): boolean => {
    if (!value) return false;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;
    return date.getFullYear() === target.getFullYear() && date.getMonth() === target.getMonth() && date.getDate() === target.getDate();
};

const isOverdue = (value?: string): boolean => {
    if (!value) return false;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date.getTime() < today.getTime();
};

const titleCase = (value?: string): string =>
    String(value || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());

const TabButton: React.FC<{ active: boolean; onClick: () => void; icon: React.ElementType; label: string }> = ({
    active,
    onClick,
    icon: Icon,
    label,
}) => (
    <button
        onClick={onClick}
        className={`inline-flex items-center gap-2 rounded-t-lg border-b-2 px-4 py-2.5 text-sm font-black transition ${
            active
                ? 'border-brand-blue bg-white text-brand-blue'
                : 'border-transparent text-slate-500 hover:bg-white/60 hover:text-slate-700'
        }`}
    >
        <Icon className="h-4 w-4" />
        {label}
    </button>
);

const FilterSelect: React.FC<{
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: Array<{ value: string; label: string }>;
}> = ({ label, value, onChange, options }) => (
    <div>
        <label className={labelClass}>{label}</label>
        <select value={value} onChange={(e) => onChange(e.target.value)} className={inputClass}>
            {options.map((option) => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    </div>
);

const EmptyState: React.FC<{ title: string; message?: string; icon?: React.ElementType }> = ({
    title,
    message,
    icon: Icon = InboxIcon,
}) => (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white py-14 text-center">
        <Icon className="h-10 w-10 text-slate-300" />
        <p className="text-sm font-black text-slate-600">{title}</p>
        {message && <p className="max-w-sm text-xs text-slate-400">{message}</p>}
    </div>
);

const KpiCard: React.FC<{
    label: string;
    value: number | string;
    icon: React.ElementType;
    tone: string;
    hint?: string;
    onClick?: () => void;
}> = ({ label, value, icon: Icon, tone, hint, onClick }) => (
    <button
        onClick={onClick}
        disabled={!onClick}
        className={`card-premium flex items-center gap-3 p-4 text-left ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
    >
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}>
            <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-wide text-slate-400">{label}</p>
            <p className="text-2xl font-black text-slate-900">{value}</p>
            {hint && <p className="truncate text-[11px] text-slate-400">{hint}</p>}
        </div>
    </button>
);

const ActivitiesPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const isManager = user ? isActivityManager(user.role) : false;
    const allowCreate = user ? canCreateAssignments(user.role) : false;
    const allowReview = user ? canReviewReports(user.role) : false;

    const requestedTab = searchParams.get('tab');
    const [activeTab, setActiveTab] = useState<TabKey>(
        TABS.some((tab) => tab.key === requestedTab) ? requestedTab as TabKey : 'overview'
    );
    const [overview, setOverview] = useState<ActivitiesOverview | null>(null);
    const [assignments, setAssignments] = useState<Assignment[]>([]);
    const [reports, setReports] = useState<ActivityReport[]>([]);
    const [types, setTypes] = useState<ActivityType[]>([]);
    const [farmers, setFarmers] = useState<FarmerOption[]>([]);
    const [fields, setFields] = useState<FarmField[]>([]);
    const [staff, setStaff] = useState<StaffUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [assignmentLoadError, setAssignmentLoadError] = useState('');
    const [reportLoadError, setReportLoadError] = useState('');

    const [assignmentSearch, setAssignmentSearch] = useState('');
    const [assignmentStatus, setAssignmentStatus] = useState(searchParams.get('status') || 'all');
    const [assignmentFarmer, setAssignmentFarmer] = useState('all');
    const [assignmentField, setAssignmentField] = useState('all');
    const [assignmentType, setAssignmentType] = useState('all');
    const [assignmentPriority, setAssignmentPriority] = useState('all');

    const [reportStatus, setReportStatus] = useState(searchParams.get('reportStatus') || 'all');
    const [reportFarmer, setReportFarmer] = useState('all');
    const [reportField, setReportField] = useState('all');
    const [reportType, setReportType] = useState('all');
    const [reportFrom, setReportFrom] = useState('');
    const [reportTo, setReportTo] = useState('');

    const [createOpen, setCreateOpen] = useState(false);
    const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(null);
    const [detailId, setDetailId] = useState<number | null>(null);
    const [reviewReportId, setReviewReportId] = useState<number | null>(null);
    const [followUpBusyId, setFollowUpBusyId] = useState<number | null>(null);
    const [typeDrawerOpen, setTypeDrawerOpen] = useState(false);
    const [editingType, setEditingType] = useState<ActivityType | null>(null);
    const [typeSaving, setTypeSaving] = useState(false);
    const [typeForm, setTypeForm] = useState<Record<string, any>>({
        name: '',
        category: 'field',
        description: '',
        requires_input: false,
        requires_photo: true,
        requires_gps: true,
        requires_start_time: true,
        requires_end_time: true,
        requires_area_completed: true,
        requires_materials: true,
        requires_equipment: false,
        requires_weather: true,
        requires_observations: true,
        min_inspection_points: 0,
        requires_before_photo: true,
        requires_after_photo: true,
        requires_signature: false,
        safety_instructions: '',
        work_instructions: '',
    });

    const loadAll = useCallback(async () => {
        setError('');
        setAssignmentLoadError('');
        setReportLoadError('');
        const [overviewRes, assignmentsRes, reportsRes, typesRes, farmersRes, fieldsRes, staffRes] = await Promise.allSettled([
            fetchOverview(),
            fetchAssignments(),
            fetchReports(),
            fetchActivityTypes(),
            fetchFarmers(),
            fetchFarmPlots(),
            fetchStaffUsers(),
        ]);
        if (overviewRes.status === 'fulfilled') setOverview(overviewRes.value);
        if (assignmentsRes.status === 'fulfilled') {
            setAssignments(assignmentsRes.value);
        } else {
            setAssignments([]);
            setAssignmentLoadError((assignmentsRes.reason as Error)?.message || 'Failed to load assignments.');
        }
        if (reportsRes.status === 'fulfilled') {
            setReports(reportsRes.value);
        } else {
            setReports([]);
            setReportLoadError((reportsRes.reason as Error)?.message || 'Failed to load reports.');
        }
        if (typesRes.status === 'fulfilled') setTypes(typesRes.value);
        if (farmersRes.status === 'fulfilled') setFarmers(farmersRes.value);
        if (fieldsRes.status === 'fulfilled') setFields(fieldsRes.value);
        if (staffRes.status === 'fulfilled') setStaff(staffRes.value);
        if (
            overviewRes.status === 'rejected' &&
            assignmentsRes.status === 'rejected' &&
            reportsRes.status === 'rejected'
        ) {
            setError((overviewRes.reason as Error)?.message || 'Failed to load field activities.');
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    useEffect(() => {
        const unsubscribe = subscribeToDataInvalidation((event) => {
            if (event.endpoint.includes('/api/activities/')) loadAll();
        });
        return unsubscribe;
    }, [loadAll]);

    const refreshReports = useCallback(async () => {
        try {
            const nextReports = await fetchReports();
            setReports(nextReports);
            setReportLoadError('');
        } catch (refreshError) {
            setReportLoadError((refreshError as Error)?.message || 'Failed to refresh reports.');
        }
    }, []);

    useEffect(() => {
        if (activeTab !== 'reports' && activeTab !== 'overview') return;
        const timer = window.setInterval(() => {
            if (document.visibilityState === 'visible') refreshReports();
        }, 30000);
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') refreshReports();
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [activeTab, refreshReports]);

    const assignmentCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        assignments.forEach((a) => {
            counts[a.status] = (counts[a.status] || 0) + 1;
        });
        return counts;
    }, [assignments]);

    const openCount = useMemo(
        () => assignments.filter((a) => !TERMINAL_STATUSES.includes(a.status)).length,
        [assignments]
    );
    const dueTodayCount = useMemo(
        () => assignments.filter((a) => !TERMINAL_STATUSES.includes(a.status) && isSameDay(a.due_date)).length,
        [assignments]
    );
    const overdueCount = useMemo(
        () => assignments.filter((a) => !TERMINAL_STATUSES.includes(a.status) && isOverdue(a.due_date)).length,
        [assignments]
    );
    const awaitingReviewCount = useMemo(
        () => reports.filter((r) => ['submitted', 'awaiting_review', 'resubmitted'].includes(r.status)).length,
        [reports]
    );
    const approvedThisMonth = useMemo(() => {
        const now = new Date();
        return assignments.filter(
            (a) =>
                a.status === 'approved' &&
                a.approved_at &&
                new Date(a.approved_at).getFullYear() === now.getFullYear() &&
                new Date(a.approved_at).getMonth() === now.getMonth()
        ).length;
    }, [assignments]);

    const filteredAssignments = useMemo(() => {
        const query = assignmentSearch.trim().toLowerCase();
        return assignments
            .filter((a) => {
                if (assignmentStatus === 'all') return true;
                if (assignmentStatus === 'active') return !TERMINAL_STATUSES.includes(a.status) && a.status !== 'draft';
                if (assignmentStatus === 'completed') return ['approved', 'closed'].includes(a.status);
                if (assignmentStatus === 'overdue') return !TERMINAL_STATUSES.includes(a.status) && isOverdue(a.due_date);
                return a.status === assignmentStatus;
            })
            .filter((a) => (assignmentFarmer === 'all' ? true : String(a.farmer_id) === assignmentFarmer))
            .filter((a) => (assignmentField === 'all' ? true : String(a.field_plot_id) === assignmentField))
            .filter((a) => (assignmentType === 'all' ? true : String(a.activity_type_id) === assignmentType))
            .filter((a) => (assignmentPriority === 'all' ? true : a.priority === assignmentPriority))
            .filter((a) =>
                query
                    ? [a.title, a.assignment_code, a.farmer_name, a.field_name, a.assigned_user_name, a.assign_user_name]
                          .filter(Boolean)
                          .some((v) => String(v).toLowerCase().includes(query))
                    : true
            )
            .sort((a, b) => String(b.due_date || '').localeCompare(String(a.due_date || '')));
    }, [assignments, assignmentSearch, assignmentStatus, assignmentFarmer, assignmentField, assignmentType, assignmentPriority]);

    const filteredReports = useMemo(() => {
        return reports
            .filter((r) => reportStatus === 'all'
                ? true
                : reportStatus === 'review_queue'
                    ? ['submitted', 'awaiting_review', 'resubmitted'].includes(r.status)
                    : r.status === reportStatus)
            .filter((r) => (reportFarmer === 'all' ? true : String(r.farmer_id) === reportFarmer))
            .filter((r) => (reportField === 'all' ? true : String(r.field_plot_id) === reportField))
            .filter((r) => (reportType === 'all' ? true : r.activity_type_name === reportType))
            .filter((r) => (reportFrom ? new Date(r.submitted_at || r.created_at) >= new Date(`${reportFrom}T00:00:00`) : true))
            .filter((r) => (reportTo ? new Date(r.submitted_at || r.created_at) <= new Date(`${reportTo}T23:59:59`) : true))
            .sort((a, b) => String(b.submitted_at || b.created_at).localeCompare(String(a.submitted_at || a.created_at)));
    }, [reports, reportStatus, reportFarmer, reportField, reportType, reportFrom, reportTo]);

    const todayQueue = useMemo(
        () =>
            assignments
                .filter((a) => !TERMINAL_STATUSES.includes(a.status) && isSameDay(a.due_date))
                .sort((a, b) => String(a.due_date || '').localeCompare(String(b.due_date || ''))),
        [assignments]
    );

    const priorityQueue = overview?.priority_queue || [];

    const awaitingReviewReports = useMemo(
        () =>
            reports
                .filter((r) => ['submitted', 'awaiting_review', 'resubmitted'].includes(r.status))
                .sort((a, b) => String(b.submitted_at || b.created_at).localeCompare(String(a.submitted_at || a.created_at)))
                .slice(0, 5),
        [reports]
    );

    const observations = useMemo(() => overview?.observation_alerts || [], [overview]);
    const [observationSeverity, setObservationSeverity] = useState('all');
    const filteredObservations = useMemo(
        () =>
            observationSeverity === 'all'
                ? observations
                : observations.filter((o) => (o.severity_level || '').toLowerCase() === observationSeverity),
        [observations, observationSeverity]
    );

    const farmerOptions = useMemo(
        () => [{ value: 'all', label: 'All farmers' }, ...farmers.map((f) => ({ value: String(f.id), label: f.full_name }))],
        [farmers]
    );
    const fieldOptions = useMemo(
        () => [
            { value: 'all', label: 'All fields' },
            ...fields.map((f) => {
                const name = f.name || f.plot_code || f.field_code || `Field #${f.id}`;
                const count = Number(f.farmer_count || (Array.isArray(f.farmers) ? f.farmers.length : 0));
                return { value: String(f.id), label: count > 1 ? `${name} · ${count} farmers` : name };
            }),
        ],
        [fields]
    );
    const typeOptions = useMemo(
        () => [{ value: 'all', label: 'All types' }, ...types.map((t) => ({ value: String(t.id), label: t.name }))],
        [types]
    );
    const reportTypeOptions = useMemo(() => {
        const names = Array.from(new Set(reports.map((r) => r.activity_type_name).filter(Boolean))) as string[];
        return [{ value: 'all', label: 'All types' }, ...names.map((v) => ({ value: v, label: v }))];
    }, [reports]);

    const openTypeEditor = (type?: ActivityType) => {
        setEditingType(type || null);
        setTypeForm({
            name: type?.name || '',
            category: type?.category || 'field',
            description: type?.description || '',
            requires_input: type?.requires_input || false,
            requires_photo: type?.requires_photo ?? true,
            requires_gps: type?.requires_gps ?? true,
            requires_start_time: type?.requires_start_time ?? true,
            requires_end_time: type?.requires_end_time ?? true,
            requires_area_completed: type?.requires_area_completed ?? true,
            requires_materials: type?.requires_materials ?? true,
            requires_equipment: type?.requires_equipment || false,
            requires_weather: type?.requires_weather ?? true,
            requires_observations: type?.requires_observations ?? true,
            min_inspection_points: type?.min_inspection_points ?? 0,
            requires_before_photo: type?.requires_before_photo ?? true,
            requires_after_photo: type?.requires_after_photo ?? true,
            requires_signature: type?.requires_signature || false,
            safety_instructions: type?.safety_instructions || '',
            work_instructions: type?.work_instructions || '',
        });
        setTypeDrawerOpen(true);
    };

    const handleSaveType = async () => {
        if (!typeForm.name?.trim()) {
            setError('Activity type name is required.');
            return;
        }
        setTypeSaving(true);
        try {
            await saveActivityType(typeForm);
            setTypeDrawerOpen(false);
            setEditingType(null);
            await loadAll();
        } catch (err: any) {
            setError(err?.message || 'Failed to save activity type.');
        } finally {
            setTypeSaving(false);
        }
    };

    const createFollowUpFromObservation = async (observation: Observation) => {
        if (!allowCreate || followUpBusyId) return;
        setFollowUpBusyId(observation.id);
        try {
            const severity = String(observation.severity_level || '').toLowerCase();
            const priority = severity === 'critical' ? 'urgent' : severity === 'high' ? 'high' : 'normal';
            const title = observation.pest_type || observation.disease_type
                ? `Follow-up: ${observation.pest_type || observation.disease_type}`
                : 'Follow-up field inspection';
            await createAssignment({
                title,
                farmer_id: observation.farmer_id || null,
                field_plot_id: observation.field_plot_id || null,
                priority,
                work_instructions: observation.recommended_follow_up || observation.comment || 'Review the observation and record the corrective field action.',
                reporting_requirements: {
                    requires_gps: true,
                    requires_after_photo: true,
                    requires_observations: true,
                },
            });
            setActiveTab('assignments');
            await loadAll();
        } catch (err: any) {
            setError(err?.message || 'Failed to create follow-up assignment.');
        } finally {
            setFollowUpBusyId(null);
        }
    };

    const totalDistribution = Math.max(1, assignments.length);

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-black text-slate-900">Field Activities</h1>
                    <p className="text-sm text-slate-500">
                        Assign work, track field execution, and review submitted reports for {user?.tenant?.name || user?.tenant?.company_name || 'this workspace'}.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={loadAll}
                        disabled={loading}
                        className="btn-secondary inline-flex items-center gap-2"
                    >
                        <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                    {allowCreate && (
                        <button
                            onClick={() => {
                                setEditingAssignment(null);
                                setCreateOpen(true);
                            }}
                            className="btn-primary inline-flex items-center gap-2"
                        >
                            <PlusIcon className="h-4 w-4" />
                            Create Assignment
                        </button>
                    )}
                </div>
            </div>

            <div className="flex gap-1 overflow-x-auto rounded-t-xl border-b border-slate-200 bg-slate-50/80 px-2">
                {TABS.map((tab) => (
                    <TabButton
                        key={tab.key}
                        active={activeTab === tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        icon={tab.icon}
                        label={tab.label}
                    />
                ))}
            </div>

            {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
            )}

            {loading && <div className="py-20 text-center text-sm text-slate-500">Loading field activities…</div>}

            {!loading && (
                <>
                    {activeTab === 'overview' && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                                <KpiCard
                                    label="Open assignments"
                                    value={openCount}
                                    icon={ClipboardDocumentListIcon}
                                    tone="bg-brand-blue/10 text-brand-blue"
                                    hint="Active across all fields"
                                    onClick={() => setActiveTab('assignments')}
                                />
                                <KpiCard
                                    label="Due today"
                                    value={dueTodayCount}
                                    icon={CalendarDaysIcon}
                                    tone="bg-sky-100 text-sky-700"
                                    hint={overdueCount ? `${overdueCount} overdue` : 'No overdue work'}
                                    onClick={() => setActiveTab('assignments')}
                                />
                                <KpiCard
                                    label="Awaiting review"
                                    value={awaitingReviewCount}
                                    icon={DocumentTextIcon}
                                    tone="bg-amber-100 text-amber-700"
                                    hint="Reports to review"
                                    onClick={() => setActiveTab('reports')}
                                />
                                <KpiCard
                                    label="Approved this month"
                                    value={approvedThisMonth}
                                    icon={CheckCircleIcon}
                                    tone="bg-emerald-100 text-emerald-700"
                                    hint="Assignments closed out"
                                />
                                <KpiCard
                                    label="Critical observations"
                                    value={observations.length}
                                    icon={ExclamationTriangleIcon}
                                    tone="bg-red-100 text-red-700"
                                    hint="High or alert triggered"
                                    onClick={() => setActiveTab('observations')}
                                />
                            </div>

                            <div className="grid gap-6 lg:grid-cols-3">
                                <div className="space-y-6 lg:col-span-2">
                                    {todayQueue.length > 0 && (
                                        <div className="rounded-xl border border-slate-200 bg-white p-4">
                                            <div className="mb-3 flex items-center justify-between">
                                                <p className="text-sm font-black text-slate-800">Today’s queue</p>
                                                <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-black text-sky-700">
                                                    <ClockIcon className="h-3 w-3" />
                                                    {todayQueue.length} due
                                                </span>
                                            </div>
                                            <div className="space-y-2">
                                                {todayQueue.slice(0, 5).map((a) => (
                                                    <button
                                                        key={a.id}
                                                        onClick={() => setDetailId(a.id)}
                                                        className="flex w-full items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5 text-left transition hover:border-brand-blue/40 hover:bg-brand-blue/5"
                                                    >
                                                        <div className="min-w-0">
                                                            <p className="truncate text-sm font-black text-slate-800">
                                                                {a.title || a.assignment_code}
                                                            </p>
                                                            <p className="truncate text-xs text-slate-500">
                                                                {[a.activity_type_name, a.farmer_name, a.field_name].filter(Boolean).join(' · ') || '—'}
                                                            </p>
                                                        </div>
                                                        <div className="flex shrink-0 items-center gap-2">
                                                            <ActivityPriorityBadge priority={a.priority} />
                                                            <ActivityStatusBadge status={a.status} size="sm" />
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {awaitingReviewReports.length > 0 && (
                                        <div className="rounded-xl border border-slate-200 bg-white p-4">
                                            <div className="mb-3 flex items-center justify-between">
                                                <p className="text-sm font-black text-slate-800">Reports awaiting review</p>
                                                <button
                                                    onClick={() => setActiveTab('reports')}
                                                    className="text-xs font-bold text-brand-blue hover:underline"
                                                >
                                                    View all
                                                </button>
                                            </div>
                                            <div className="space-y-2">
                                                {awaitingReviewReports.map((r) => (
                                                    <button
                                                        key={r.id}
                                                        onClick={() => setReviewReportId(r.id)}
                                                        className="flex w-full items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5 text-left transition hover:border-amber-400/50 hover:bg-amber-50/40"
                                                    >
                                                        <div className="min-w-0">
                                                            <p className="truncate text-sm font-black text-slate-800">
                                                                {r.report_code || `Report #${r.id}`}
                                                            </p>
                                                            <p className="truncate text-xs text-slate-500">
                                                                {[r.assignment_title, r.farmer_name, r.field_name].filter(Boolean).join(' · ') || '—'}
                                                            </p>
                                                        </div>
                                                        <span className="shrink-0 text-xs text-slate-400">{formatDateTime(r.submitted_at)}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {priorityQueue.length > 0 && (
                                        <div className="rounded-xl border border-red-200 bg-white p-4">
                                            <div className="mb-3 flex items-center gap-2">
                                                <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
                                                <p className="text-sm font-black text-slate-800">Priority queue</p>
                                                <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-black text-red-700">
                                                    {priorityQueue.length} high/urgent
                                                </span>
                                            </div>
                                            <div className="space-y-2">
                                                {priorityQueue.slice(0, 5).map((a) => (
                                                    <button
                                                        key={a.id}
                                                        onClick={() => setDetailId(a.id)}
                                                        className="flex w-full items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5 text-left transition hover:border-red-300 hover:bg-red-50/40"
                                                    >
                                                        <div className="min-w-0">
                                                            <p className="truncate text-sm font-black text-slate-800">
                                                                {a.title || a.assignment_code}
                                                            </p>
                                                            <p className="truncate text-xs text-slate-500">
                                                                {[a.farmer_name, a.field_name].filter(Boolean).join(' · ') || '—'}
                                                                {a.due_date ? ` · due ${formatDate(a.due_date)}` : ''}
                                                            </p>
                                                        </div>
                                                        <ActivityStatusBadge status={a.status} size="sm" />
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {todayQueue.length === 0 && awaitingReviewReports.length === 0 && priorityQueue.length === 0 && (
                                        <EmptyState
                                            title="No work on the radar"
                                            message="Nothing is due today, awaiting review, or flagged as high priority."
                                        />
                                    )}
                                </div>

                                <div className="space-y-6">
                                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                                        <p className="mb-3 text-sm font-black text-slate-800">Assignment status</p>
                                        <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
                                            {ASSIGNMENT_STATUSES.map((status) => {
                                                const count = assignmentCounts[status] || 0;
                                                if (!count) return null;
                                                return (
                                                    <div
                                                        key={status}
                                                        title={`${titleCase(status)}: ${count}`}
                                                        style={{ width: `${(count / totalDistribution) * 100}%` }}
                                                        className={`h-full ${STATUS_COLORS[status] || 'bg-slate-300'}`}
                                                    />
                                                );
                                            })}
                                        </div>
                                        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1.5">
                                            {ASSIGNMENT_STATUSES.map((status) => {
                                                const count = assignmentCounts[status];
                                                if (!count) return null;
                                                return (
                                                    <span key={status} className="inline-flex items-center gap-1 text-[11px] text-slate-600">
                                                        <span className={`h-2 w-2 rounded-full ${STATUS_COLORS[status] || 'bg-slate-300'}`} />
                                                        {titleCase(status)} · {count}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                        <p className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-400">
                                            Total {assignments.length} assignments · {openCount} open · {approvedThisMonth} approved this month
                                        </p>
                                    </div>

                                    {overview?.top_fields && overview.top_fields.length > 0 && (
                                        <div className="rounded-xl border border-slate-200 bg-white p-4">
                                            <p className="mb-3 text-sm font-black text-slate-800">Top fields</p>
                                            <div className="space-y-2">
                                                {overview.top_fields.map((f) => (
                                                    <div key={f.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
                                                        <div className="min-w-0">
                                                            <p className="truncate text-sm font-semibold text-slate-800">{f.name}</p>
                                                            <p className="text-[11px] text-slate-400">{f.field_code || `Field #${f.id}`}</p>
                                                        </div>
                                                        <div className="shrink-0 text-right">
                                                            <p className="text-sm font-black text-slate-800">{f.assignment_count}</p>
                                                            <p className="text-[10px] text-slate-400">{f.approved_count} approved</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'assignments' && (
                        <div className="space-y-4">
                            {assignmentLoadError && (
                                <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
                                    <p className="font-black">Assignments could not be loaded</p>
                                    <p className="mt-1 text-red-600">{assignmentLoadError}</p>
                                    <button
                                        type="button"
                                        onClick={loadAll}
                                        className="mt-3 rounded-lg bg-red-600 px-4 py-2 text-xs font-black text-white hover:bg-red-700"
                                    >
                                        Retry
                                    </button>
                                </div>
                            )}
                            <div className="card-premium p-4">
                                <div className="mb-3 flex flex-wrap items-center gap-2">
                                    <FunnelIcon className="h-4 w-4 text-slate-400" />
                                    <p className="text-sm font-black text-slate-800">Filters</p>
                                    <button
                                        onClick={() => {
                                            setAssignmentSearch('');
                                            setAssignmentStatus('all');
                                            setAssignmentFarmer('all');
                                            setAssignmentField('all');
                                            setAssignmentType('all');
                                            setAssignmentPriority('all');
                                        }}
                                        className="ml-auto text-xs font-bold text-brand-blue hover:underline"
                                    >
                                        Reset
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
                                    <div className="lg:col-span-1">
                                        <label className={labelClass}>Search</label>
                                        <div className="relative">
                                            <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                                            <input
                                                value={assignmentSearch}
                                                onChange={(e) => setAssignmentSearch(e.target.value)}
                                                placeholder="Title, code, farmer…"
                                                className={`${inputClass} pl-8`}
                                            />
                                        </div>
                                    </div>
                                    <FilterSelect
                                        label="Status"
                                        value={assignmentStatus}
                                        onChange={setAssignmentStatus}
                                        options={[{ value: 'all', label: 'All statuses' }, { value: 'active', label: 'Active work' }, { value: 'completed', label: 'Completed' }, { value: 'overdue', label: 'Overdue' }, ...ASSIGNMENT_STATUSES.map((s) => ({ value: s, label: titleCase(s) }))]}
                                    />
                                    <FilterSelect
                                        label="Farmer"
                                        value={assignmentFarmer}
                                        onChange={setAssignmentFarmer}
                                        options={farmerOptions}
                                    />
                                    <FilterSelect
                                        label="Field"
                                        value={assignmentField}
                                        onChange={setAssignmentField}
                                        options={fieldOptions}
                                    />
                                    <FilterSelect
                                        label="Type"
                                        value={assignmentType}
                                        onChange={setAssignmentType}
                                        options={typeOptions}
                                    />
                                    <FilterSelect
                                        label="Priority"
                                        value={assignmentPriority}
                                        onChange={setAssignmentPriority}
                                        options={[{ value: 'all', label: 'All priorities' }, ...PRIORITIES.map((p) => ({ value: p, label: titleCase(p) }))]}
                                    />
                                </div>
                            </div>

                            <div className="card-premium overflow-hidden p-0">
                                {filteredAssignments.length === 0 && !assignmentLoadError ? (
                                    <div className="p-6">
                                        <EmptyState
                                            title="No assignments found"
                                            message="Adjust your filters or create a new assignment."
                                        />
                                    </div>
                                ) : !assignmentLoadError ? (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left text-sm">
                                            <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-wide text-slate-400">
                                                <tr>
                                                    <th className="px-4 py-3">Assignment</th>
                                                    <th className="px-4 py-3">Farmer / Field</th>
                                                    <th className="px-4 py-3">Assignee</th>
                                                    <th className="px-4 py-3">Due</th>
                                                    <th className="px-4 py-3">Priority</th>
                                                    <th className="px-4 py-3">Status</th>
                                                    {allowCreate && <th className="px-4 py-3 text-right">Actions</th>}
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {filteredAssignments.slice(0, 100).map((a) => (
                                                    <tr
                                                        key={a.id}
                                                        onClick={() => setDetailId(a.id)}
                                                        className="cursor-pointer transition hover:bg-brand-blue/5"
                                                    >
                                                        <td className="px-4 py-3">
                                                            <p className="font-black text-slate-800">{a.title || a.assignment_code}</p>
                                                            <p className="text-xs text-slate-400">
                                                                {a.assignment_code}
                                                                {a.activity_type_name ? ` · ${a.activity_type_name}` : ''}
                                                            </p>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <p className="font-semibold text-slate-700">{a.farmer_name || '—'}</p>
                                                            <p className="text-xs text-slate-400">{a.field_name || a.plot_code || '—'}</p>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <p className="inline-flex items-center gap-1 text-xs text-slate-600">
                                                                <UserIcon className="h-3.5 w-3.5 text-slate-400" />
                                                                {a.assigned_user_name || a.assign_user_name || a.assigned_staff_name || 'Unassigned'}
                                                            </p>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span
                                                                className={`text-xs font-bold ${
                                                                    a.status !== 'closed' && a.status !== 'cancelled' && a.status !== 'approved' && a.status !== 'rejected' && isOverdue(a.due_date)
                                                                        ? 'text-red-600'
                                                                        : isSameDay(a.due_date)
                                                                          ? 'text-brand-blue'
                                                                          : 'text-slate-500'
                                                                }`}
                                                            >
                                                                {formatDate(a.due_date)}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <ActivityPriorityBadge priority={a.priority} />
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <ActivityStatusBadge status={a.status} />
                                                        </td>
                                                        {allowCreate && (
                                                            <td className="px-4 py-3 text-right">
                                                                <button
                                                                    type="button"
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        setEditingAssignment(a);
                                                                        setCreateOpen(false);
                                                                    }}
                                                                    disabled={TERMINAL_STATUSES.includes(a.status)}
                                                                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-black text-slate-600 shadow-sm transition hover:border-brand-blue/40 hover:text-brand-blue disabled:cursor-not-allowed disabled:opacity-50"
                                                                    title={
                                                                        TERMINAL_STATUSES.includes(a.status)
                                                                            ? `Cannot edit ${titleCase(a.status)} assignment`
                                                                            : 'Edit assignment'
                                                                    }
                                                                >
                                                                    <PencilSquareIcon className="h-3.5 w-3.5" />
                                                                    Edit
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : null}
                            </div>
                            <p className="text-xs text-slate-400">
                                Showing {filteredAssignments.length} of {assignments.length} assignments
                            </p>
                        </div>
                    )}

                    {activeTab === 'reports' && (
                        <div className="space-y-4">
                            {reportLoadError && (
                                <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                                    <span>{reportLoadError}</span>
                                    <button type="button" onClick={refreshReports} className="font-bold hover:underline">
                                        Retry
                                    </button>
                                </div>
                            )}
                            <div className="card-premium p-4">
                                <div className="mb-3 flex flex-wrap items-center gap-2">
                                    <FunnelIcon className="h-4 w-4 text-slate-400" />
                                    <p className="text-sm font-black text-slate-800">Filters</p>
                                    <button
                                        onClick={() => {
                                            setReportStatus('all');
                                            setReportFarmer('all');
                                            setReportField('all');
                                            setReportType('all');
                                            setReportFrom('');
                                            setReportTo('');
                                        }}
                                        className="ml-auto text-xs font-bold text-brand-blue hover:underline"
                                    >
                                        Reset
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
                                    <FilterSelect
                                        label="Status"
                                        value={reportStatus}
                                        onChange={setReportStatus}
                                        options={[{ value: 'all', label: 'All statuses' }, { value: 'review_queue', label: 'Awaiting review' }, ...REPORT_STATUSES.map((s) => ({ value: s, label: titleCase(s) }))]}
                                    />
                                    <FilterSelect
                                        label="Farmer"
                                        value={reportFarmer}
                                        onChange={setReportFarmer}
                                        options={farmerOptions}
                                    />
                                    <FilterSelect
                                        label="Field"
                                        value={reportField}
                                        onChange={setReportField}
                                        options={fieldOptions}
                                    />
                                    <FilterSelect
                                        label="Type"
                                        value={reportType}
                                        onChange={setReportType}
                                        options={reportTypeOptions}
                                    />
                                    <div>
                                        <label className={labelClass}>Submitted from</label>
                                        <input type="date" value={reportFrom} onChange={(e) => setReportFrom(e.target.value)} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Submitted to</label>
                                        <input type="date" value={reportTo} onChange={(e) => setReportTo(e.target.value)} className={inputClass} />
                                    </div>
                                </div>
                            </div>

                            <div className="card-premium overflow-hidden p-0">
                                {filteredReports.length === 0 ? (
                                    <div className="p-6">
                                        <EmptyState
                                            title="No reports found"
                                            message="Reports submitted by the field team will appear here."
                                        />
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left text-sm">
                                            <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-wide text-slate-400">
                                                <tr>
                                                    <th className="px-4 py-3">Report</th>
                                                    <th className="px-4 py-3">Assignment</th>
                                                    <th className="px-4 py-3">Farmer / Field</th>
                                                    <th className="px-4 py-3">Submitted</th>
                                                    <th className="px-4 py-3">Quantity vs planned</th>
                                                    <th className="px-4 py-3">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {filteredReports.slice(0, 100).map((r) => (
                                                    <tr
                                                        key={r.id}
                                                        onClick={() => setReviewReportId(r.id)}
                                                        className="cursor-pointer transition hover:bg-amber-50/30"
                                                    >
                                                        <td className="px-4 py-3">
                                                            <p className="font-black text-slate-800">{r.report_code || `Report #${r.id}`}</p>
                                                            <p className="text-xs text-slate-400">
                                                                {r.activity_type_name || '—'}
                                                                {r.submitted_by_name ? ` · ${r.submitted_by_name}` : ''}
                                                            </p>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <p className="font-semibold text-slate-700">{r.assignment_title || r.assignment_code || '—'}</p>
                                                            <p className="text-xs text-slate-400">{r.assignment_code || '—'}</p>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <p className="font-semibold text-slate-700">{r.farmer_name || '—'}</p>
                                                            <p className="text-xs text-slate-400">{r.field_name || '—'}</p>
                                                        </td>
                                                        <td className="px-4 py-3 text-xs text-slate-500">{formatDateTime(r.submitted_at)}</td>
                                                        <td className="px-4 py-3">
                                                            {r.variance_percent !== undefined && r.variance_percent !== null ? (
                                                                <span
                                                                    className={`rounded-full px-2 py-0.5 text-[11px] font-black ${
                                                                        Math.abs(Number(r.variance_percent)) > 30
                                                                            ? 'bg-orange-100 text-orange-700'
                                                                            : 'bg-emerald-100 text-emerald-700'
                                                                    }`}
                                                                >
                                                                    {Number(r.variance_percent) > 0 ? '+' : ''}
                                                                    {Number(r.variance_percent).toFixed(0)}%
                                                                </span>
                                                            ) : (
                                                                <span className="text-xs text-slate-300">—</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <ActivityStatusBadge status={r.status} />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                            <p className="text-xs text-slate-400">
                                Showing {filteredReports.length} of {reports.length} reports
                            </p>
                        </div>
                    )}

                    {activeTab === 'observations' && (
                        <div className="space-y-4">
                            <div className="card-premium p-4">
                                <div className="mb-3 flex flex-wrap items-center gap-2">
                                    <ExclamationTriangleIcon className="h-4 w-4 text-slate-400" />
                                    <p className="text-sm font-black text-slate-800">Field observations</p>
                                </div>
                                <div className="grid grid-cols-2 gap-3 sm:max-w-xs">
                                    <FilterSelect
                                        label="Severity"
                                        value={observationSeverity}
                                        onChange={setObservationSeverity}
                                        options={[
                                            { value: 'all', label: 'All severities' },
                                            { value: 'critical', label: 'Critical' },
                                            { value: 'high', label: 'High' },
                                            { value: 'medium', label: 'Medium' },
                                            { value: 'low', label: 'Low' },
                                        ]}
                                    />
                                </div>
                            </div>

                            {filteredObservations.length === 0 ? (
                                <EmptyState
                                    title="No critical observations"
                                    message="Sample-point findings that trigger alerts will show up here."
                                />
                            ) : (
                                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                    {filteredObservations.map((o: Observation) => (
                                        <div key={o.id} className="card-premium p-4">
                                            <div className="mb-2 flex items-center justify-between gap-2">
                                                <span
                                                    className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${
                                                        (o.severity_level || '').toLowerCase() === 'critical'
                                                            ? 'bg-red-100 text-red-700'
                                                            : 'bg-orange-100 text-orange-700'
                                                    }`}
                                                >
                                                    {(o.severity_level || 'observed').toLowerCase()}
                                                </span>
                                                <span className="text-[11px] text-slate-400">{formatDateTime(o.created_at)}</span>
                                            </div>
                                            <p className="flex items-center gap-1.5 text-sm font-black text-slate-800">
                                                <MapPinIcon className="h-4 w-4 text-brand-blue" />
                                                {o.field_name || 'Unknown field'}
                                            </p>
                                            {o.assignment_code && <p className="mt-0.5 text-xs text-slate-400">{o.assignment_code}</p>}
                                            <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                                                {o.pest_type && (
                                                    <p>
                                                        <span className="font-bold">Pest:</span> {o.pest_type}
                                                        {o.pest_severity ? ` (${o.pest_severity})` : ''}
                                                    </p>
                                                )}
                                                {o.disease_type && (
                                                    <p>
                                                        <span className="font-bold">Disease:</span> {o.disease_type}
                                                        {o.disease_severity ? ` (${o.disease_severity})` : ''}
                                                    </p>
                                                )}
                                                {o.damaged_area !== undefined && o.damaged_area !== null && (
                                                    <p>
                                                        <span className="font-bold">Affected area:</span> {Number(o.damaged_area).toFixed(2)} ha
                                                    </p>
                                                )}
                                                {o.crop_stage && (
                                                    <p>
                                                        <span className="font-bold">Crop stage:</span> {o.crop_stage}
                                                    </p>
                                                )}
                                            </div>
                                            {o.recommended_follow_up && (
                                                <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                                                    Follow-up: {o.recommended_follow_up}
                                                </p>
                                            )}
                                            {allowCreate && (
                                                <button
                                                    onClick={() => createFollowUpFromObservation(o)}
                                                    disabled={followUpBusyId === o.id}
                                                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-brand-blue/20 bg-brand-blue/5 px-3 py-2 text-xs font-black text-brand-blue transition hover:bg-brand-blue/10 disabled:cursor-not-allowed disabled:opacity-60"
                                                >
                                                    {followUpBusyId === o.id && <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />}
                                                    {followUpBusyId === o.id ? 'Creating follow-up...' : 'Create follow-up assignment'}
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'types' && (
                        <div className="space-y-4">
                            <div className="card-premium flex flex-wrap items-center justify-between gap-3 p-4">
                                <div>
                                    <p className="text-sm font-black text-slate-800">Activity type configuration</p>
                                    <p className="text-xs text-slate-500">
                                        Define the data, evidence, GPS, safety, and report fields required for each field activity.
                                    </p>
                                </div>
                                {allowCreate && (
                                    <button
                                        onClick={() => openTypeEditor()}
                                        className="btn-primary inline-flex items-center gap-2"
                                    >
                                        <PlusIcon className="h-4 w-4" />
                                        New activity type
                                    </button>
                                )}
                            </div>
                            <div className="card-premium overflow-hidden p-0">
                                {types.length === 0 ? (
                                    <div className="p-6">
                                        <EmptyState
                                            title="No activity types yet"
                                            message="Activity types define what the field team must capture when they submit a report."
                                        />
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left text-sm">
                                            <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-wide text-slate-400">
                                                <tr>
                                                    <th className="px-4 py-3">Name</th>
                                                    <th className="px-4 py-3">Category</th>
                                                    <th className="px-4 py-3">Capture requirements</th>
                                                    <th className="px-4 py-3">Description</th>
                                                    <th className="px-4 py-3">Status</th>
                                                    {allowCreate && <th className="px-4 py-3 text-right">Actions</th>}
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {types.map((t) => {
                                                    const flags = [
                                                        t.requires_input && 'Inputs',
                                                        t.requires_photo && 'Photos',
                                                        t.requires_gps && 'GPS',
                                                        t.requires_start_time && 'Start time',
                                                        t.requires_end_time && 'End time',
                                                        t.requires_area_completed && 'Area',
                                                        t.requires_materials && 'Materials',
                                                        t.requires_equipment && 'Equipment',
                                                        t.requires_weather && 'Weather',
                                                        t.requires_observations && 'Observations',
                                                        t.requires_before_photo && 'Before photo',
                                                        t.requires_after_photo && 'After photo',
                                                        t.requires_signature && 'Signature',
                                                    ].filter(Boolean) as string[];
                                                    return (
                                                        <tr key={t.id} className="transition hover:bg-brand-blue/5">
                                                            <td className="px-4 py-3">
                                                                <p className="font-black text-slate-800">{t.name}</p>
                                                                <p className="text-xs text-slate-400">Type #{t.id}</p>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">
                                                                    {t.category || 'General'}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="flex max-w-md flex-wrap gap-1">
                                                                    {flags.length === 0 && <span className="text-xs text-slate-300">—</span>}
                                                                    {flags.map((flag) => (
                                                                        <span key={flag} className="rounded bg-brand-blue/10 px-1.5 py-0.5 text-[10px] font-bold text-brand-blue">
                                                                            {flag}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </td>
                                                            <td className="max-w-xs px-4 py-3">
                                                                <p className="line-clamp-2 text-xs text-slate-500">{t.description || '—'}</p>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <span
                                                                    className={`rounded-full px-2 py-0.5 text-[10px] font-black ${
                                                                        t.is_active === false ? 'bg-slate-100 text-slate-500' : 'bg-emerald-100 text-emerald-700'
                                                                    }`}
                                                                >
                                                                    {t.is_active === false ? 'Inactive' : 'Active'}
                                                                </span>
                                                            </td>
                                                            {allowCreate && (
                                                                <td className="px-4 py-3 text-right">
                                                                    <button
                                                                        onClick={() => openTypeEditor(t)}
                                                                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-black text-slate-600 transition hover:border-brand-blue/30 hover:text-brand-blue"
                                                                    >
                                                                        Edit
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                            <p className="text-xs text-slate-400">{types.length} activity types configured</p>
                        </div>
                    )}
                </>
            )}

            {allowCreate && (
                <AssignmentFormDrawer
                    open={createOpen || editingAssignment !== null}
                    onClose={() => {
                        setCreateOpen(false);
                        setEditingAssignment(null);
                    }}
                    types={types}
                    farmers={farmers}
                    fields={fields}
                    users={staff}
                    assignment={editingAssignment}
                    onCreated={() => {
                        setCreateOpen(false);
                        setEditingAssignment(null);
                        loadAll();
                    }}
                />
            )}

            <AssignmentDetailDrawer
                open={detailId !== null}
                assignmentId={detailId}
                onClose={() => setDetailId(null)}
                canManage={isManager}
                fields={fields}
                onReviewReport={(reportId) => {
                    setDetailId(null);
                    setReviewReportId(reportId);
                }}
                onDataChanged={() => loadAll()}
            />

            {(allowReview || isManager) && (
                <ReportReviewDrawer
                    open={reviewReportId !== null}
                    reportId={reviewReportId}
                    onClose={() => setReviewReportId(null)}
                    canManage={isManager}
                    fields={fields}
                    onDataChanged={() => loadAll()}
                    onCreateFollowUp={(report, dailyReport) => {
                        const followUpText =
                            dailyReport?.issues_and_follow_up?.recommended_follow_up ||
                            dailyReport?.issues_and_follow_up?.corrective_action ||
                            dailyReport?.management_review?.correction_request ||
                            report.corrective_action ||
                            report.problem_encountered ||
                            'Follow up on the submitted field activity report and record corrective action with GPS and photo evidence.';
                        const severityPriority =
                            Number(dailyReport?.field_conditions_and_observations?.high_severity_points || 0) > 0
                                ? 'high'
                                : report.assistance_required
                                  ? 'high'
                                  : 'normal';
                        setReviewReportId(null);
                        setEditingAssignment({
                            id: 0,
                            assignment_code: '',
                            title: `Follow-up: ${report.report_code || report.assignment_code || 'field report'}`,
                            farmer_id: report.farmer_id,
                            farmer_name: report.farmer_name,
                            field_plot_id: report.field_plot_id,
                            field_name: report.field_name,
                            field_code: report.field_code,
                            crop_type:
                                dailyReport?.assignment_information?.crop ||
                                report.assignment_crop_type ||
                                report.field_crop_type,
                            priority: severityPriority as Assignment['priority'],
                            status: 'draft',
                            planned_start_date: new Date().toISOString().slice(0, 10),
                            due_date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
                            work_instructions: String(followUpText),
                            reporting_requirements: {
                                requires_gps: true,
                                requires_after_photo: true,
                                requires_observations: true,
                            },
                            created_at: new Date().toISOString(),
                        });
                    }}
                />
            )}

            {allowCreate && (
                <ActivityTypeDrawer
                    open={typeDrawerOpen}
                    editing={Boolean(editingType)}
                    value={typeForm}
                    saving={typeSaving}
                    onChange={setTypeForm}
                    onClose={() => setTypeDrawerOpen(false)}
                    onSave={handleSaveType}
                />
            )}
        </div>
    );
};

export default ActivitiesPage;
